import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { IconChevronRight } from './Icons';
import type { PagedResult } from '../api/types';

// =====================================================================================
// Pagination
// =====================================================================================

export function Pagination<T>({
  result,
  onPageChange,
}: {
  result: PagedResult<T>;
  onPageChange: (page: number) => void;
}) {
  if (result.totalPages <= 1) return null;

  const pages = pageWindow(result.page, result.totalPages);

  return (
    <nav aria-label="Search results pages" className="mt-4">
      <ul className="pagination justify-content-center mb-1">
        <li className={`page-item ${result.hasPrevious ? '' : 'disabled'}`}>
          <button
            type="button"
            className="page-link"
            onClick={() => onPageChange(result.page - 1)}
            disabled={!result.hasPrevious}
          >
            Previous
          </button>
        </li>

        {pages.map((page, index) =>
          page === null ? (
            <li className="page-item disabled" key={`gap-${index}`}>
              <span className="page-link">…</span>
            </li>
          ) : (
            <li className={`page-item ${page === result.page ? 'active' : ''}`} key={page}>
              <button
                type="button"
                className="page-link"
                onClick={() => onPageChange(page)}
                aria-current={page === result.page ? 'page' : undefined}
              >
                {page}
              </button>
            </li>
          ),
        )}

        <li className={`page-item ${result.hasNext ? '' : 'disabled'}`}>
          <button
            type="button"
            className="page-link"
            onClick={() => onPageChange(result.page + 1)}
            disabled={!result.hasNext}
          >
            Next
          </button>
        </li>
      </ul>

      <p className="text-center sf-muted small mb-0">
        Showing {(result.page - 1) * result.pageSize + 1}–
        {Math.min(result.page * result.pageSize, result.totalCount)} of {result.totalCount}
      </p>
    </nav>
  );
}

/** First, last, and a window around the current page, with gaps marked as null. */
function pageWindow(current: number, total: number): (number | null)[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages = new Set<number>([1, total, current, current - 1, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);

  const result: (number | null)[] = [];
  let previous = 0;

  for (const page of sorted) {
    if (previous && page - previous > 1) result.push(null);
    result.push(page);
    previous = page;
  }

  return result;
}

// =====================================================================================
// Confirmation dialog
// =====================================================================================

/**
 * Used before anything destructive. Focus moves into the dialog on open and Escape
 * cancels, so it is usable without a mouse.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  busy = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    confirmRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <>
      <div className="modal-backdrop show" />
      <div className="modal d-block" role="dialog" aria-modal="true" aria-labelledby="confirm-title">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title h5" id="confirm-title">
                {title}
              </h2>
            </div>
            <div className="modal-body">{message}</div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={onCancel} disabled={busy}>
                {cancelLabel}
              </button>
              <button
                type="button"
                ref={confirmRef}
                className={`btn ${destructive ? 'btn-danger' : 'btn-primary'}`}
                onClick={onConfirm}
                disabled={busy}
              >
                {busy ? 'Working…' : confirmLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// =====================================================================================
// Form helpers
// =====================================================================================

export function Field({
  label,
  htmlFor,
  required,
  hint,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  required?: boolean;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="mb-3">
      <label className={`form-label ${required ? 'sf-required' : ''}`} htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {hint && !error && <div className="form-text">{hint}</div>}
      {error && (
        <div className="invalid-feedback d-block" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}

export function Checkbox({
  id,
  label,
  checked,
  onChange,
  hint,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  hint?: string;
}) {
  return (
    <div className="form-check mb-2">
      <input
        className="form-check-input"
        type="checkbox"
        id={id}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <label className="form-check-label" htmlFor={id}>
        {label}
      </label>
      {hint && <div className="form-text">{hint}</div>}
    </div>
  );
}

/** Multi-select rendered as toggleable chips, used throughout the filter panels. */
export function ChipGroup<T extends string | number>({
  legend,
  options,
  selected,
  onChange,
  multiple = true,
}: {
  legend: string;
  options: { value: T; label: string }[];
  selected: T[];
  onChange: (values: T[]) => void;
  multiple?: boolean;
}) {
  const toggle = (value: T) => {
    if (!multiple) {
      onChange(selected.includes(value) ? [] : [value]);
      return;
    }

    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };

  return (
    <fieldset className="mb-0">
      <legend className="sf-visually-hidden">{legend}</legend>
      <div className="d-flex flex-wrap gap-2">
        {options.map((option) => {
          const active = selected.includes(option.value);

          return (
            <button
              type="button"
              key={String(option.value)}
              className={`btn btn-sm ${active ? 'btn-primary' : 'btn-outline-secondary'}`}
              aria-pressed={active}
              onClick={() => toggle(option.value)}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

// =====================================================================================
// Misc
// =====================================================================================

/**
 * The heading block every portal page opens with.
 *
 * The layout's top bar carries identity and navigation, not the page title, so each page
 * states what it is here. Keeping that in one component is what stops the three portals
 * drifting into three different header treatments.
 */
export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="sf-pagehead">
      <div className="sf-pagehead__text">
        <h1 className="sf-pagehead__title">{title}</h1>
        {description && <p className="sf-pagehead__desc">{description}</p>}
      </div>
      {actions && <div className="sf-pagehead__actions">{actions}</div>}
    </div>
  );
}

/**
 * The greeting at the top of the dashboard.
 *
 * Compact on purpose: it is read once and scrolled past, so it earns two lines rather than
 * a hero banner. Display the supplied name in full and do not invent one when it is missing.
 */
export function WelcomeHeader({ name, subtitle }: { name?: string; subtitle?: string }) {
  const displayName = name?.trim();

  return (
    <div className="sf-welcome">
      <h1 className="sf-welcome__title">
        {displayName ? `Welcome back, ${displayName}` : 'Welcome back'}
      </h1>
      <p className="sf-welcome__sub">
        {subtitle ?? 'Manage your properties and enquiries easily.'}
      </p>
    </div>
  );
}

/**
 * A shortcut to a task.
 *
 * These overlap with the sidebar on purpose: the sidebar is for moving around, these are
 * for starting something, and the description says what that something is.
 */
export function QuickAction({
  to,
  icon,
  label,
  description,
}: {
  to: string;
  icon: ReactNode;
  label: string;
  description: string;
}) {
  return (
    <Link className="sf-quick" to={to}>
      <span className="sf-quick__icon" aria-hidden="true">{icon}</span>
      <span className="sf-quick__body">
        <span className="sf-quick__label">{label}</span>
        <span className="sf-quick__desc">{description}</span>
      </span>
      <IconChevronRight className="sf-quick__chevron" aria-hidden="true" />
    </Link>
  );
}

export function StatCard({
  value,
  label,
  alert = false,
  hint,
}: {
  value: string | number;
  label: string;
  alert?: boolean;
  hint?: string;
}) {
  return (
    <div className={`sf-stat ${alert ? 'sf-stat--alert' : ''}`}>
      <div className="sf-stat__value">{value}</div>
      <div className="sf-stat__label">{label}</div>
      {hint && <div className="form-text mt-1">{hint}</div>}
    </div>
  );
}

/** Copies the current listing URL, falling back to a prompt where the API is unavailable. */
export function ShareButton({ title, className = 'btn btn-outline-secondary' }: { title: string; className?: string }) {
  const [copied, setCopied] = useState(false);

  const share = async () => {
    const url = window.location.href;

    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch {
        // The person dismissed the sheet; fall through to copying.
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Copy this link', url);
    }
  };

  return (
    <button type="button" className={className} onClick={share}>
      {copied ? 'Link copied' : 'Share'}
    </button>
  );
}
